import React from 'react';

import { useQuery } from '@apollo/client';
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { WATERFALL_DRILL_DOWN_QUERY } from '../../graphql/meetings';

interface WaterfallDrillDownProps {
  open: boolean;
  onClose: () => void;
  barLabel: string;
  dateFrom: string;
  dateTo: string;
}

const WaterfallDrillDown: React.FC<WaterfallDrillDownProps> = ({ open, onClose, barLabel, dateFrom, dateTo }) => {
  const { data, loading } = useQuery(WATERFALL_DRILL_DOWN_QUERY, {
    variables: { barLabel, dateFrom, dateTo },
    skip: !open || !barLabel,
  });

  const rows = (data?.waterfallDrillDown ?? []).map((row: any, idx: number) => ({
    id: row.companyId ?? idx,
    companyId: row.companyId,
    companyName: row.companyName,
    domain: row.domain,
    meetingHolders: (row.meetingHolders ?? []).join(', '),
    emailSenders: (row.emailSenders ?? []).join(', '),
    emailReceivers: (row.emailReceivers ?? []).join(', '),
  }));

  const columns: GridColDef[] = [
    {
      field: 'companyName',
      headerName: 'Company Name',
      flex: 1,
      minWidth: 200,
      renderCell: params => (
        <Link href={`/evaluation/dfm?companyId=${params.row.companyId}`} underline="hover">
          {params.value}
        </Link>
      ),
    },
    { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 150 },
    { field: 'meetingHolders', headerName: 'Meeting Holders', flex: 1, minWidth: 200 },
    { field: 'emailSenders', headerName: 'Email Senders', flex: 1, minWidth: 180 },
    { field: 'emailReceivers', headerName: 'Email Receivers', flex: 1, minWidth: 180 },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {barLabel}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaterfallDrillDown;
